import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ArrowLeft, ExternalLink, Copy, MessageCircle, Activity, Send, Share2, Award } from 'lucide-react';
import { API, absoluteImageUrl } from '../lib/api';
import { networkOf, tokenUrl, uniswapUrl } from '../lib/networks';
import { copyToClipboard } from '../lib/clipboard';
import { xShareUrl, tokenPageUrl } from '../lib/share';
import { useWallet } from '../context/WalletContext';
import { shortAddr } from '../lib/wallet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import LpRecoveryPanel from '../components/LpRecoveryPanel';

const SESSION_KEY = 'cf_chat_session';

const TokenDetail = () => {
  const { address } = useParams();
  const [searchParams] = useSearchParams();
  // ?chain= disambiguates tokens sharing an address across chains
  const chainQ = searchParams.get('chain');
  const chainArg = chainQ ? `chain_id=${chainQ}` : '';
  const nav = useNavigate();
  const { wallet } = useWallet();
  const [token, setToken] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [history, setHistory] = useState([]);
  const [range, setRange] = useState(24); // hours: 24 or 168
  const [holdings, setHoldings] = useState(null);
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  // Load token metadata
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/tokens/${address}${chainArg ? `?${chainArg}` : ''}`);
        if (!cancelled) setToken(data);
      } catch (_) {
        if (!cancelled) setToken(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [address, chainArg]);

  // Creator profile + badges
  useEffect(() => {
    if (!token?.creator_address) { setCreator(null); return; }
    let cancelled = false;
    axios.get(`${API}/creators/${token.creator_address}`)
      .then(({ data }) => { if (!cancelled) setCreator(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  // Live price + REAL snapshot history, refreshed every 60s.
  // Requests are independent — a slow RPC on /price must not block the chart.
  useEffect(() => {
    let cancelled = false;
    const loadLive = () => {
      axios.get(`${API}/tokens/${address}/price${chainArg ? `?${chainArg}` : ''}`)
        .then(({ data }) => { if (!cancelled) setPriceData(data); })
        .catch(() => {});
      axios.get(`${API}/tokens/${address}/price/history?hours=${range}${chainArg ? `&${chainArg}` : ''}`)
        .then(({ data }) => { if (!cancelled) setHistory(data.points || []); })
        .catch(() => {});
    };
    loadLive();
    const id = setInterval(loadLive, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [address, range, chainArg]);

  // Wallet holdings (if connected + on the same chain)
  useEffect(() => {
    if (!wallet?.provider || !token || wallet.chainId !== token.chain_id) { setHoldings(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = '0x70a08231' + wallet.address.toLowerCase().replace('0x', '').padStart(64, '0');
        const raw = await wallet.provider.send('eth_call', [{ to: token.contract_address, data }, 'latest']);
        if (cancelled) return;
        const bal = BigInt(raw || '0x0');
        setHoldings(Number(bal / 10n ** 18n));
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [wallet, token]);

  const net = token ? networkOf(token.chain_id) : null;
  const priceEth = Number(priceData?.price_eth_per_token || 0);
  const mcapEth = Number(priceData?.market_cap_eth || 0);
  const img = absoluteImageUrl(token?.image_url);

  // REAL chart data from recorded on-chain price snapshots (5-min backend snapshotter).
  const chartData = useMemo(() => history.map((pt) => ({
    time: new Date(pt.t).toLocaleString([], range > 24
      ? { weekday: 'short', hour: '2-digit' }
      : { hour: '2-digit', minute: '2-digit' }),
    price: Number(pt.price_eth),
  })), [history, range]);

  // % change over the selected range (first vs last snapshot).
  const priceChange = useMemo(() => {
    if (chartData.length < 2) return null;
    const first = chartData[0].price;
    const last = chartData[chartData.length - 1].price;
    if (!(first > 0)) return null;
    return ((last - first) / first) * 100;
  }, [chartData]);

  if (loading) {
    return <div className="max-w-6xl mx-auto px-6 py-20 text-forge-muted animate-pulse">Loading token…</div>;
  }
  if (!token) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-20 text-center">
        <p className="text-forge-muted">Token not found.</p>
        <Link to="/explore" className="cf-btn-secondary mt-4 inline-flex">Back to Explore</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <button onClick={() => nav('/explore')} className="cf-btn-ghost text-sm mb-6" data-testid="back-to-explore">
        <ArrowLeft className="w-4 h-4" /> Explore
      </button>

      <div className="cf-card p-6 mb-6 flex flex-wrap items-center gap-5">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#7DF9FF]/15 to-[#A78BFA]/15 border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
          {img ? <img src={img} alt={token.name} className="w-full h-full object-cover" />
               : <span className="font-display font-bold text-3xl cf-gradient-text">{token.symbol?.[0] || '?'}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-3xl font-extrabold truncate">{token.name}</h1>
            <span className="cf-chip">{net.short}</span>
          </div>
          <p className="text-forge-muted text-sm mt-1 flex items-center gap-2">
            <span className="font-medium text-white">${token.symbol}</span>
            <span className="font-code">{shortAddr(token.contract_address)}</span>
            <button onClick={() => copyToClipboard(token.contract_address, 'Contract')} className="text-forge-muted hover:text-[#7DF9FF]" aria-label="copy contract"><Copy className="w-3 h-3" /></button>
          </p>
        </div>
        <div className="flex gap-2">
          <a
            data-testid="detail-share-x"
            href={xShareUrl(token)}
            target="_blank"
            rel="noopener noreferrer"
            className="cf-btn-secondary text-sm !py-2"
          >
            <Share2 className="w-4 h-4" /> Share
          </a>
          <button
            data-testid="detail-copy-link"
            onClick={() => copyToClipboard(tokenPageUrl(token.contract_address, token.chain_id), 'Token link')}
            className="cf-btn-ghost text-sm !py-2"
            aria-label="copy token link"
          >
            <Copy className="w-4 h-4" />
          </button>
          {uniswapUrl(token.chain_id, token.contract_address) && (
            <a href={uniswapUrl(token.chain_id, token.contract_address)} target="_blank" rel="noopener noreferrer" className="cf-btn-primary text-sm !py-2">
              <ExternalLink className="w-4 h-4" /> Trade
            </a>
          )}
          <a href={tokenUrl(token.chain_id, token.contract_address)} target="_blank" rel="noopener noreferrer" className="cf-btn-secondary text-sm !py-2">
            <ExternalLink className="w-4 h-4" /> Explorer
          </a>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-white/[0.04] border border-white/10 p-1 rounded-full w-fit">
          <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-full px-5 data-[state=active]:bg-white/10">
            <Activity className="w-4 h-4 mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat" className="rounded-full px-5 data-[state=active]:bg-white/10">
            <MessageCircle className="w-4 h-4 mr-1.5" /> Ask Forge about this coin
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-6">
          <LpRecoveryPanel
            contractAddress={token.contract_address}
            chainId={token.chain_id}
            creatorAddress={token.creator_address}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Stat
              label="Price"
              value={priceEth > 0 ? `${priceEth.toExponential(4)} ETH` : '—'}
              hint={priceChange != null
                ? `${priceChange >= 0 ? '+' : ''}${priceChange.toFixed(2)}% over ${range > 24 ? '7d' : '24h'}`
                : (priceData?.error || 'per token')}
              hintClass={priceChange != null ? (priceChange >= 0 ? 'text-emerald-400' : 'text-[#FF6B7A]') : ''}
              testid="stat-price" tone="cyan" />
            <Stat label="Market cap" value={mcapEth > 0 ? `${mcapEth.toFixed(4)} ETH` : '—'} hint={`${Number(token.total_supply).toLocaleString()} supply`} testid="stat-mcap" tone="violet" />
            <Stat label="Your holdings" value={holdings != null ? holdings.toLocaleString() : '—'} hint={wallet && wallet.chainId === token.chain_id ? `${token.symbol}` : 'connect on same chain'} testid="stat-holdings" tone="pink" />
          </div>

          <div className="cf-card p-5" data-testid="price-chart">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <p className="text-xs text-forge-muted uppercase tracking-wider font-medium flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                Live on-chain price
              </p>
              <div className="flex gap-1" data-testid="chart-range-toggle">
                {[{ h: 24, label: '24H' }, { h: 168, label: '7D' }].map((r) => (
                  <button
                    key={r.h}
                    data-testid={`chart-range-${r.label}`}
                    onClick={() => setRange(r.h)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      range === r.h
                        ? 'bg-[#7DF9FF]/15 border-[#7DF9FF]/40 text-[#7DF9FF]'
                        : 'bg-white/[0.03] border-white/10 text-forge-muted hover:text-white'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            {chartData.length >= 2 ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={chartData}>
                  <defs>
                    <linearGradient id="cfLine" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#7DF9FF" />
                      <stop offset="60%" stopColor="#A78BFA" />
                      <stop offset="100%" stopColor="#F472B6" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="time" stroke="#8E93B8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#8E93B8" fontSize={11} tickLine={false} axisLine={false} width={80}
                         domain={['auto', 'auto']}
                         tickFormatter={(v) => v.toExponential(1)} />
                  <Tooltip
                    contentStyle={{ background: 'rgba(11,10,42,0.95)', border: '1px solid rgba(125,249,255,0.3)', borderRadius: 12, fontSize: 12 }}
                    labelStyle={{ color: '#8E93B8' }}
                    formatter={(v) => [`${Number(v).toExponential(4)} ETH`, 'price']}
                  />
                  <Line type="monotone" dataKey="price" stroke="url(#cfLine)" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : priceEth > 0 ? (
              <div className="py-16 text-center text-sm" data-testid="chart-collecting">
                <p className="text-white font-medium">Live price: {priceEth.toExponential(4)} ETH</p>
                <p className="text-forge-muted mt-1.5">Collecting on-chain snapshots — a new point is recorded every ~5 minutes. The timeline fills in automatically.</p>
              </div>
            ) : (
              <div className="py-16 text-center text-forge-muted text-sm">No price data yet — pool isn&apos;t seeded on this chain.</div>
            )}
          </div>

          {creator && (
            <div className="cf-card p-5" data-testid="creator-badges">
              <div className="flex flex-wrap items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FBBF24]/20 to-[#F472B6]/10 border border-[#FBBF24]/30 flex items-center justify-center shrink-0">
                  <Award className="w-5 h-5 text-[#FBBF24]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-forge-muted uppercase tracking-wider font-medium">Creator</p>
                  <p className="font-code text-sm text-white flex items-center gap-1.5">
                    {shortAddr(token.creator_address)}
                    <button onClick={() => copyToClipboard(token.creator_address, 'Creator')} className="text-forge-muted hover:text-[#7DF9FF]" aria-label="copy creator"><Copy className="w-3 h-3" /></button>
                    <span className="text-forge-muted font-body">· {creator.coins_launched} coin{creator.coins_launched === 1 ? '' : 's'} forged</span>
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {creator.badges.map((b) => (
                    <span
                      key={b.id}
                      data-testid={`badge-${b.id}`}
                      title={b.desc}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-[#FBBF24]/10 border border-[#FBBF24]/30 text-[#FBBF24] flex items-center gap-1"
                    >
                      <Award className="w-3 h-3" /> {b.label}
                    </span>
                  ))}
                  {creator.badges.length === 0 && (
                    <span className="text-xs text-forge-muted">No badges yet</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {priceData?.pool_address && (
            <p className="text-xs text-forge-muted">
              Uniswap V3 pool: <span className="font-code text-white">{shortAddr(priceData.pool_address)}</span>
              <button onClick={() => copyToClipboard(priceData.pool_address, 'Pool')} className="ml-1 text-forge-muted hover:text-[#7DF9FF]"><Copy className="w-3 h-3 inline" /></button>
            </p>
          )}
        </TabsContent>

        <TabsContent value="chat" className="mt-6">
          <ContextualChat token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Contextualized chat for the token detail page. Uses the same streaming API
// but prepends the coin's context (name, symbol, chain, contract) into the
// first user message so Forge can answer coin-specific questions.
const ContextualChat = ({ token }) => {
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: `Hey — you're looking at ${token.name} ($${token.symbol}) on ${networkOf(token.chain_id).name}. Ask me anything about its economics, how to buy, or the on-chain contract.`,
  }]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const sessionId = useMemo(() => {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const s = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, s);
    return s;
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    const context = `[coin-context] name=${token.name} symbol=${token.symbol} chain=${networkOf(token.chain_id).name} contract=${token.contract_address}\n\n${text}`;
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: context }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n\n');
        buf = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          try {
            const j = JSON.parse(line.slice(5).trim());
            if (j.t) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last?.role === 'assistant') copy[copy.length - 1] = { ...last, content: last.content + j.t };
                return copy;
              });
            }
          } catch (_) { /* skip */ }
        }
      }
    } catch (_) {
      setMessages((m) => [...m.slice(0, -1), { role: 'assistant', content: 'Chat is offline. Try again in a moment.' }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cf-card p-5 flex flex-col h-[560px]" data-testid="contextual-chat">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
              m.role === 'user'
                ? 'bg-gradient-to-br from-[#7DF9FF] to-[#A78BFA] text-[#04121A] font-medium rounded-br-md'
                : 'bg-white/[0.06] text-white border border-white/5 rounded-bl-md'
            }`}>
              {m.content || <span className="italic text-forge-muted">thinking…</span>}
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 pt-3 border-t border-white/10 mt-3">
        <input
          data-testid="contextual-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Ask about ${token.symbol}…`}
          className="cf-input !py-2 !text-sm !rounded-full"
          disabled={busy}
        />
        <button
          data-testid="contextual-chat-send"
          type="submit"
          disabled={busy || !input.trim()}
          className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#7DF9FF] to-[#A78BFA] text-[#04121A] flex items-center justify-center disabled:opacity-40"
          aria-label="send"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};

const Stat = ({ label, value, hint, hintClass = '', testid, tone }) => {
  const tones = {
    cyan: 'from-[#7DF9FF]/15 to-[#7DF9FF]/[0.03] border-[#7DF9FF]/25',
    violet: 'from-[#A78BFA]/15 to-[#A78BFA]/[0.03] border-[#A78BFA]/25',
    pink: 'from-[#F472B6]/15 to-[#F472B6]/[0.03] border-[#F472B6]/25',
  };
  return (
    <div data-testid={testid} className={`rounded-2xl bg-gradient-to-br ${tones[tone]} border p-4`}>
      <p className="text-xs text-forge-muted uppercase tracking-wider font-medium">{label}</p>
      <p className="font-display text-2xl font-bold mt-1 text-white truncate">{value}</p>
      {hint && <p className={`text-xs mt-0.5 truncate ${hintClass || 'text-forge-muted'}`}>{hint}</p>}
    </div>
  );
};

export default TokenDetail;
